<template>
  <div class="plate-code-container">
    <v-select
      v-model="selectedPlateCode"
      :items="plateCodeOptions"
      item-title="title"
      item-value="value"
      label="Plate Code *"
      :rules="[requiredRule]"
      class="custom-field"
      dense
      @update:model-value="handlePlateCodeChange"
    >
    </v-select>
    
    <!-- Custom Entry Field - Fixed condition and binding -->
    <v-text-field
      v-if="selectedPlateCode === 'custom'"
      v-model="customPlateCode"
      label="Enter Custom Plate Code *"
      placeholder="Enter your custom plate code"
      :rules="[requiredRule]"
      class="custom-field mt-2"
      dense
      @update:model-value="handleCustomInput"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
});

const selectedPlateCode = ref(null);
const customPlateCode = ref('');

// Generate alphabet options (A-Z)
const alphabetOptions = Array.from({ length: 26 }, (_, i) => {
  const letter = String.fromCharCode(65 + i);
  return { title: letter, value: letter };
});

// Double letter options
const doubleLetterOptions = [
  { title: 'AA', value: 'AA' },
  { title: 'BB', value: 'BB' },
  { title: 'CC', value: 'CC' },
  { title: 'DD', value: 'DD' }
];

// Custom entry option
const customOption = { title: 'Custom Entry', value: 'custom' };

// Combine all options
const plateCodeOptions = [
  ...alphabetOptions,
  ...doubleLetterOptions,
  customOption
];

const requiredRule = (value) => !!value || 'This field is required.';

const handlePlateCodeChange = (value) => {
  console.log('Plate code changed to:', value);
  
  if (value === 'custom') {
    // When custom is selected, emit the custom text field value (or empty string)
    emit('update:modelValue', customPlateCode.value || '');
  } else if (value) {
    // When a predefined option is selected, emit that value and clear custom input
    emit('update:modelValue', value);
    customPlateCode.value = ''; // Clear custom input when predefined option selected
  }
};

const handleCustomInput = (value) => {
  console.log('Custom input changed to:', value);
  // Only emit if custom is actually selected
  if (selectedPlateCode.value === 'custom') {
    emit('update:modelValue', value || '');
  }
};

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  console.log('Model value changed externally to:', newValue);
  
  // Check if the new value is a predefined option
  const predefinedOption = plateCodeOptions.find(option => option.value === newValue && option.value !== 'custom');
  
  if (predefinedOption) {
    // It's a predefined option
    selectedPlateCode.value = newValue;
    customPlateCode.value = '';
  } else if (newValue && newValue !== 'custom') {
    // It's a custom value (not empty and not 'custom')
    selectedPlateCode.value = 'custom';
    customPlateCode.value = newValue;
  } else if (newValue === 'custom') {
    // The value is literally 'custom' (shouldn't happen, but handle it)
    selectedPlateCode.value = 'custom';
    customPlateCode.value = '';
  } else {
    // Empty or null value
    selectedPlateCode.value = null;
    customPlateCode.value = '';
  }
}, { immediate: true });

// Watch customPlateCode changes to ensure consistency
watch(customPlateCode, (newValue) => {
  // Only emit if custom is selected and we're not in the middle of a prop update
  if (selectedPlateCode.value === 'custom') {
    emit('update:modelValue', newValue || '');
  }
});
</script>

<style scoped>
.plate-code-container {
  width: 100%;
}

.custom-field {
  width: 100%;
}

/* Add some spacing for the custom input field */
.mt-2 {
  margin-top: 12px !important;
}

/* Ensure custom field has proper styling */
.custom-field.mt-2 {
  margin-top: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 8px !important;
}
</style>